/// <reference path="../types.d.ts" />

import { createReducer } from '@reduxjs/toolkit';
import InitialState from './initial-state';
import * as dictionaryValues from '../actions/dictionary-values';
import * as dictionaryRequest from '../actions/dictionary-request.actions';
import { LOAD_STATES } from './common';

import { jumpContext } from '../actions/contexts.action';
import { logout } from '../actions/ui';
import * as dictionary from '../actions/dictionary';
import { dictionaryMetadata } from '../tests/fixtures/stubs/dictionary';
import { commonEnums } from '@codametrix/ui-common';
import { defaultUploadResponse } from './state/dictionary-request';
import { formErrors } from '../stubs/form-errors';

const { DictionaryEditStatus, DictionaryStatus } = commonEnums;
const replaceFieldOptions = (
  form: CMxCommonApp.FormDefintion,
  fieldKey: string,
  options: any
) => {
  const colMetadtaFormFields = (form as CMxCommonApp.FormDefintion).fields;
  const formIndex = colMetadtaFormFields?.findIndex(
    field => field.key === fieldKey
  );
  if (formIndex !== undefined && colMetadtaFormFields !== undefined) {
    return colMetadtaFormFields.map((field, index) => {
      if (index === formIndex) {
        return { ...field, options };
      }
      return {
        ...field,
        options: formIndex < index ? [] : field.options
      };
    });
  }
  return colMetadtaFormFields;
};

const reducer = createReducer(InitialState.dictionaryRequest, builder => {
  // Default cases from cmxReducerFactory: reset state on logout or context jump
  builder.addCase(logout.type, () => ({ ...InitialState.dictionaryRequest }));
  builder.addCase(jumpContext.pending.type, () => ({
    ...InitialState.dictionaryRequest
  }));

  builder.addCase(dictionaryRequest.uploadDictionary.pending, state => {
    return {
      ...state,
      formErrors: formErrors.noErrors,
      loadState: LOAD_STATES.started
    };
  });

  builder.addCase(dictionaryRequest.uploadDictionary.rejected, (state, action) => {
    const res = action.error as any;
    return {
      ...state,
      formErrors: res.errors,
      loadState: LOAD_STATES.failed
    };
  });

  builder.addCase(dictionaryRequest.uploadDictionary.fulfilled, (state, action) => {
    const requestResponse = action.payload;
    return {
      ...state,
      requestResponse,
      formErrors: formErrors.noErrors,
      loadState: LOAD_STATES.done
    };
  });

  builder.addCase(dictionaryRequest.fetchRequestStatus.fulfilled, (state, action) => {
    const requestResponse = action.payload;
    return {
      ...state,
      requestResponse
    };
  });

  builder.addCase(dictionaryValues.publishDictionary.fulfilled, (state, action) => {
    const requestResponse = action.payload;
    return {
      ...state,
      requestResponse
    };
  });

  builder.addCase(dictionaryValues.initUpload.pending, state => {
    return {
      ...state,
      loadState: LOAD_STATES.initial,
      requestResponse: {
        ...defaultUploadResponse,
        requestType: 'UPLOAD',
        requestStatus: 'INITIALIZED'
      }
    };
  });

  builder.addCase(dictionaryValues.initUpload.fulfilled, (state, action) => {
    //If a column has multiple cell references this mapping might not suffice.
    const params = action.meta.arg;
    const colMetadata: CMxAPI.ColumnDefinitionMetadata[] = params.columnDefinitions.map(
      col => {
        return {
          columnName: col.name,
          keyColumnIndicator: col.keyColumnIndicator,
          cellReference: col.cellReferences[0]
        };
      }
    );
    const dm: CMxAPI.DictionaryMetadata = {
      tenantId: params.tenantId.uuid,
      dictionaryName: params.name,
      description: params.description,
      purpose: params.purpose,
      crossMapIndicator: params.crossMapIndicator,
      columnDefinitionMetadata: colMetadata
    };
    return {
      ...state,
      loadState: LOAD_STATES.done,
      formErrors: formErrors.noErrors,
      dictionaryMetadata: dm,
      isUploadCreate: colMetadata.length ? false : true
    };
  });

  builder.addCase(dictionaryValues.listDictionaryOptions.fulfilled, (state, action: any) => {
    const res = action.payload;
    const params = action.meta.arg;
    const { columnMetadataForm, dictionaryMetadata } = state;

    const publishedDictionaries = res.pageableList.content.filter(
      (res: any) => {
        return (
          res.status !== DictionaryStatus.ARCHIVED.toUpperCase() &&
          res.editStatus !== DictionaryEditStatus.RETIRE.toUpperCase()
        );
      }
    );

    const fields = replaceFieldOptions(
      columnMetadataForm,
      'referenceDictionary',
      publishedDictionaries
    );

    return {
      ...state,
      dictionaryOptions: publishedDictionaries,
      dictionaryMetadata: {
        ...dictionaryMetadata,
        tenantId: params.tenantId
      },
      selectedTenantName: res.organizationName,
      columnMetadataForm: {
        ...state.columnMetadataForm,
        fields
      }
    };
  });

  builder.addCase(dictionaryValues.listColumnOptions.fulfilled, (state, action) => {
    const res = action.payload;
    const { columnMetadataForm } = state;

    const fields = replaceFieldOptions(
      columnMetadataForm,
      'referenceColumn',
      res
    );

    return {
      ...state,
      columnMetadataForm: {
        ...state.columnMetadataForm,
        fields
      }
    };
  });

  builder.addCase(dictionary.listItems.pending, state => {
    return {
      ...state,
      dictionaryMetadata
    };
  });

  builder.addCase(dictionaryValues.addColumnMetadata.pending, state => {
    return {
      ...state,
      colMetadataLoadState: LOAD_STATES.started
    };
  });

  builder.addCase(dictionaryValues.addColumnMetadata.fulfilled, (state, action) => {
    const params = action.meta.arg;
    const { dictionaryMetadata, columnMetadataForm } = state;

    const fields = replaceFieldOptions(
      columnMetadataForm,
      'referenceDictionary',
      []
    );
    return {
      ...state,
      dictionaryMetadata: {
        ...dictionaryMetadata,
        columnDefinitionMetadata: [
          ...dictionaryMetadata.columnDefinitionMetadata,
          params
        ]
      },
      columnMetadataForm: {
        ...columnMetadataForm,
        fields
      },
      colMetadataLoadState: LOAD_STATES.initial
    };
  });

  builder.addCase(dictionaryValues.removeColumnMetadata, (state, action) => {
    const res = action.payload;
    const { dictionaryMetadata } = state;

    return {
      ...state,
      dictionaryMetadata: {
        ...dictionaryMetadata,
        columnDefinitionMetadata: dictionaryMetadata.columnDefinitionMetadata.filter(
          (e: CMxAPI.ColumnDefinitionMetadata) => e !== res
        )
      }
    };
  });

  builder.addCase(dictionaryValues.chooseColumnReference, (state, action) => {
    return {
      ...state
    };
  });

  builder.addCase(dictionary.getTenantList.pending, state => {
    return {
      ...state,
      loadState: LOAD_STATES.started
    };
  });
  builder.addCase(dictionary.getTenantList.rejected, (state, action) => {
    const res = action.error as any;
    return {
      ...state,
      formErrors: res.errors,
      loadState: LOAD_STATES.failed
    };
  });
  builder.addCase(dictionary.getTenantList.fulfilled, (state, action) => {
    const result = action.payload;
    const { columnMetadataForm } = state;

    const fields = replaceFieldOptions(
      columnMetadataForm,
      'referenceTenant',
      result
    );
    return {
      ...state,
      columnMetadataForm: {
        ...columnMetadataForm,
        fields
      },
      loadState: LOAD_STATES.done
    };
  });
});

export default reducer;
